import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './styles/global.css';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { AuthKitProvider } from '@workos-inc/authkit-react';
import { BrowserRouter } from 'react-router-dom';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import App from './App';
import { cloudhouseTheme } from './theme';
import { runtimeEnv } from './runtimeEnv';
import { initAppInsights, applyAnalyticsConsent } from './lib/telemetry';
import { readConsent } from './lib/consent/consentStore';
import { ConsentProvider } from './lib/consent/ConsentProvider';
import { ConsentBanner } from './components/ConsentBanner';
import { applyPreviewFromUrl } from './lib/preview';
import { hubLdContext } from './lib/ldContext';
import { originRedirectUri } from './lib/originRedirectUri';
import { captureTrialIntent, reportUnrecognisedTrialIntent } from './lib/trialIntent';

// Before any analytics: strips the #email= fragment the website trial form appends.
captureTrialIntent(globalThis.location);

initAppInsights();
reportUnrecognisedTrialIntent(globalThis.location);
// Gated analytics (Clarity, GA4) start only if this browser already consented; otherwise the
// banner collects a choice. App Insights + LaunchDarkly run unconditionally (legitimate interest).
if (readConsent()?.choice === 'granted') applyAnalyticsConsent('granted');

// ?preview=true|false toggles a persistent cookie; surface it to LD as a `preview`
// context attribute so flags can target preview sessions pre-auth.
const preview = applyPreviewFromUrl();

const LDApp = withLDProvider({
  clientSideID: runtimeEnv.ldClientId,
  // Stable per-browser anonymous key (persisted) so LD can bucket visitors for
  // rollouts/experiments/funnel conversion — not a single shared key.
  // Built by hubLdContext so the Landing page can find this context's cached flags (ldFlagCache).
  context: hubLdContext(preview),
  options: { bootstrap: 'localStorage' },
  // Feature Flags standard, SDK resilience: never wait for initialisation without a bound.
  // On a fresh origin (no cached flags) a stalled connection would otherwise leave the
  // provider's client undefined forever and every settled-flags wait spinning with it.
  // After the timeout the provider still hands over the client; flags read their defaults.
  timeout: 5,
})(App);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={cloudhouseTheme} defaultColorScheme="light">
      <Notifications position="top-right" />
      <AuthKitProvider
        clientId={runtimeEnv.workosClientId}
        redirectUri={originRedirectUri(runtimeEnv.workosRedirectUri, globalThis.location.origin)}
        apiHostname={runtimeEnv.workosApiHostname || undefined}
        port={runtimeEnv.workosPort}
        https={runtimeEnv.workosHttps}
      >
        <BrowserRouter>
          <ConsentProvider>
            <LDApp />
            <ConsentBanner />
          </ConsentProvider>
        </BrowserRouter>
      </AuthKitProvider>
    </MantineProvider>
  </StrictMode>,
);
